/* eslint-disable max-statements */
import { safeLocalStorage } from '@/core/safeLocalStorage/safeLocalStorage';
import { useTracker } from '@/core/tracking';
import { useCallToActionModal } from '@/features/callToAction/useCallToActionModal';
import {
  useEditorService,
  useMediaService,
  usePaymentService,
  useUploaderService,
} from '@/layout/appWrapper/ServiceProvider';
import modalBackgroundImage from '@/public/images/covers/batch-processing-cover.webp';
import { useLocalization } from '@/services/localizationService';
import {
  createSubmitTaskPayload,
  ID_PREFIX,
  ImageTaskSubmitPayload,
  MaxFileQuantityForUpload,
  TaskError,
  TaskStatus,
  TaskType,
} from '@/services/uploaderService';
import { UserAction, useUser } from '@/services/userService';
import { inputMediaNamesState, mediaState } from '@/stores/mediaStore';
import { pushModalSelector } from '@/stores/modalStore';
import settingsState from '@/stores/settingsStore';
import taskStateFamily from '@/stores/taskStateFamilyStore';
import { useRouter } from 'next/router';
import React, { useCallback, useRef } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import UploadMediaErrorModal from '../UploadMediaError';
import { enhanceFlowTracker } from '../trackers';

export const useAddPhotosButton = () => {
  const { track } = useTracker();
  const { t, data } = useLocalization();
  const media = useRecoilValue(mediaState);
  const { showCallToActionModal } = useCallToActionModal();
  const router = useRouter();
  const { taskId } = router.query;
  const settings = useRecoilValue(settingsState);
  const { extractAiPipelineFromAiToolsChoices, useEditorSettings } =
    useEditorService();
  const { buildDefaultAiToolsChoices } = useEditorSettings();
  const [, setTask] = useRecoilState(taskStateFamily('result'));
  const pushModal = useSetRecoilState(pushModalSelector);
  const mainActionRef = useRef<any>(null);
  const { addMediasToBulkUpload, useUploader } = useUploaderService();
  const { setTaskInfoFromFiles, onBulkUploadComplete, handleBulkUploadErrors } =
    useUploader(setTask, pushModal, mainActionRef);

  const { isBusinessUser, isPersonalUser } = usePaymentService();

  const [mediaName, setMediaName] = useRecoilState(inputMediaNamesState);
  const { data: user } = useUser();
  const { useMedia } = useMediaService();
  const { getBulkUploadWithPolling } = useMedia();

  const isBusiness = isBusinessUser(user);
  const isPersonal = isPersonalUser(user);

  const onClickAddPhotos = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      (e.target as HTMLInputElement).value = '';
      if (isBusiness) {
        document.getElementById('add-photos')?.click();
      } else {
        e.preventDefault();
        const callToActionModalConfig = isPersonal
          ? {
              id: 'user:subscribeToBusinessToBulkUpload',
              previousAction: UserAction.BatchProcessing,
              backgroundImage: modalBackgroundImage,
              title: t(data.common.batchProcessingAvailableForBusiness),
              body: t(
                data.common.batchProcessingAvailableToBusinessDescription
              ),
              showPhotoEnhanceButton: true,
              showSubscribeFlowButton: false,
              showUpgradePlanButton: true,
              hasFreeTrial: false,
              showLoginCTA: false,
              uploaderId: `${ID_PREFIX}-result-${taskId}`,
            }
          : {
              id: 'user:subscribeToBusinessToBulkUpload',
              previousAction: UserAction.BatchProcessing,
              backgroundImage: modalBackgroundImage,
              title: t(data.common.batchProcessingAvailableForBusiness),
              body: t(
                data.common.batchProcessingAvailableToBusinessDescription
              ),
              showPhotoEnhanceButton: true,
              showSubscribeFlowButton: true,
              showUpgradePlanButton: false,
              hasFreeTrial: false,
              showLoginCTA: true,
              uploaderId: `${ID_PREFIX}-result-${taskId}`,
            };
        track('batch_processing_cta_popup', 'shown');
        showCallToActionModal(callToActionModalConfig);
        return;
      }
      track('add_photos', 'tapped');
    },
    [
      data.common.batchProcessingAvailableForBusiness,
      data.common.batchProcessingAvailableToBusinessDescription,
      isBusiness,
      isPersonal,
      showCallToActionModal,
      t,
      taskId,
      track,
    ]
  );

  const handleAddPhotos = useCallback(
    async (files: FileList | null) => {
      track('add_photos', 'tapped');

      if (!files) return;

      const { shouldPromptFileErrorPopup } = handleBulkUploadErrors({
        files,
        track,
      });

      if (shouldPromptFileErrorPopup) {
        return;
      }

      if (media.id && media.taskList) {
        const isLimitReachedWithNewFiles =
          media.taskList.length + files.length > MaxFileQuantityForUpload;
        if (isLimitReachedWithNewFiles) {
          pushModal({
            id: 'task:uploadError',
            Modal: UploadMediaErrorModal,
            maxFileQuantityLeftForUpload:
              MaxFileQuantityForUpload - media.taskList.length,
            error: TaskError.TooManyFiles,
            onClose: () => mainActionRef.current?.focus(),
          });
          return;
        }
      }

      setTaskInfoFromFiles(files);

      const fileArray = Object.values(files);
      // this fills the mediaNameArray with the newly added names
      const fileNameArray = fileArray.map((f) => {
        const imageFileName = f.name.substring(0, f.name.lastIndexOf('.'));
        return `${imageFileName}.remini-enhanced`;
      });
      const newMediaNameArray = [...mediaName, ...fileNameArray];
      setMediaName(newMediaNameArray);

      enhanceFlowTracker.setInfo({
        media_type: 'image',
        from: 'addPhotos',
        has_multiple_medias: files.length > 1,
      } as const);

      track('upload', TaskStatus.Started, {
        actionValue: 'addPhotos',
        mediaType: TaskType.Image,
      });

      const aiToolsChoices = buildDefaultAiToolsChoices();
      const fullAiPipeline = extractAiPipelineFromAiToolsChoices(
        aiToolsChoices,
        settings.values,
        {}
      );

      const submitBulkUploadPayload = fileArray.map((file) =>
        createSubmitTaskPayload({
          isVideo: false,
          file,
          remoteSettings: settings,
          isBulkUpload: fileArray.length > 1,
          aiPipeline: fullAiPipeline,
          defaultUserAiPipeline: settings.values
            .shouldEnableSavingAiModelDefault
            ? user?.preferences?.defaultAiPipeline
            : undefined,
        })
      );

      let bulkUpload;
      try {
        enhanceFlowTracker.startUpload();
        bulkUpload = await addMediasToBulkUpload({
          bulkUploadId: taskId as string,
          inputTaskList: submitBulkUploadPayload as ImageTaskSubmitPayload[],
        });
        enhanceFlowTracker.completeUpload();
      } catch (e) {
        enhanceFlowTracker.failUpload(e);
        throw e;
      }

      let newBulk;
      try {
        enhanceFlowTracker.startProcess();
        newBulk = await onBulkUploadComplete({
          fileArray,
          bulkUpload,
          track,
          taskId: taskId as string,
          showProgress: true,
        });
      } catch (e) {
        enhanceFlowTracker.failProcess(e);
        throw e;
      }

      const isBulkTaskListCompleted = newBulk?.taskList?.every(
        (t) => t.status === TaskStatus.Completed
      );
      if (isBulkTaskListCompleted) {
        enhanceFlowTracker.completeProcess();
      } else {
        getBulkUploadWithPolling({ isBulkProcessingCompleted: false }).then(
          () => enhanceFlowTracker.completeProcess()
        );
      }

      setTask({ status: TaskStatus.Pristine });
      safeLocalStorage.setItem('isAutomaticScrollEnabled', 'true');
    },
    [
      addMediasToBulkUpload,
      buildDefaultAiToolsChoices,
      extractAiPipelineFromAiToolsChoices,
      getBulkUploadWithPolling,
      handleBulkUploadErrors,
      media.id,
      media.taskList,
      mediaName,
      onBulkUploadComplete,
      pushModal,
      setMediaName,
      setTask,
      setTaskInfoFromFiles,
      settings,
      taskId,
      track,
      user?.preferences?.defaultAiPipeline,
    ]
  );

  return {
    handleAddPhotos,
    text: t(data.common.addPhotos),
    onClickAddPhotos,
    allowMultipleMediaSelection: isBusiness,
  };
};
